import type { MapPoiType } from '@/lib/types/map-poi'
import { getStrapiMedia } from '@/lib/strapi/client'
import { MIN_BOUNDS_PAD_DEG } from './constants'

export function isVisiblePoi(poi: MapPoiType): boolean {
  return poi.hidePoi !== true
}

export function poiCoordinates(
  poi: Pick<MapPoiType, 'coordinates'>
): google.maps.LatLngLiteral | null {
  const lat = poi.coordinates?.latitude
  const lng = poi.coordinates?.longitude
  if (lat == null || lng == null) return null
  return { lat, lng }
}

export function boundsFromPoints(
  points: google.maps.LatLngLiteral[]
): google.maps.LatLngBoundsLiteral | null {
  if (points.length === 0) return null

  return {
    north: Math.max(...points.map((p) => p.lat)),
    south: Math.min(...points.map((p) => p.lat)),
    east: Math.max(...points.map((p) => p.lng)),
    west: Math.min(...points.map((p) => p.lng)),
  }
}

export function boundsFromPois(
  pois: Array<Pick<MapPoiType, 'coordinates'>>
): google.maps.LatLngBoundsLiteral | null {
  return boundsFromPoints(
    pois.flatMap((poi) => {
      const coords = poiCoordinates(poi)
      return coords ? [coords] : []
    })
  )
}

export function boundsCenter(
  bounds: google.maps.LatLngBoundsLiteral
): google.maps.LatLngLiteral {
  return {
    lat: (bounds.north + bounds.south) / 2,
    lng: (bounds.east + bounds.west) / 2,
  }
}

/** Tight box around a point, or a point plus existing POI bounds. */
export function boundsIncludingPoint(
  point: google.maps.LatLngLiteral,
  bounds: google.maps.LatLngBoundsLiteral | null
): google.maps.LatLngBoundsLiteral {
  if (!bounds) {
    return {
      north: point.lat + MIN_BOUNDS_PAD_DEG,
      south: point.lat - MIN_BOUNDS_PAD_DEG,
      east: point.lng + MIN_BOUNDS_PAD_DEG,
      west: point.lng - MIN_BOUNDS_PAD_DEG,
    }
  }

  return {
    north: Math.max(bounds.north, point.lat),
    south: Math.min(bounds.south, point.lat),
    east: Math.max(bounds.east, point.lng),
    west: Math.min(bounds.west, point.lng),
  }
}

export function filterByCategory(
  pois: MapPoiType[],
  category: string
): MapPoiType[] {
  if (category === 'All') return pois

  return pois.filter((poi) => poi.category?.name === category)
}

export function isHighlightedPoi(poi: MapPoiType): boolean {
  return poi.highlightPoi === true
}

export function highlightedPois(pois: MapPoiType[]): MapPoiType[] {
  const seen = new Set<string>()

  return pois.filter((poi) => {
    if (poi.highlightPoi === false) return false

    const key = poi.title ?? String(poi.id)
    if (seen.has(key)) return false

    seen.add(key)
    return true
  })
}

/**
 * Distance copy is often `"0.5 miles / 10 min walk"`.
 * The Home map list shows only the last segment (`"10 min walk"`).
 */
export function lastDistanceSegment(text: string | null | undefined): string | null {
  if (!text?.trim()) return null

  const parts = text
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean)

  return parts.at(-1) ?? null
}

export function deriveCategories(pois: MapPoiType[]) {
  const seen = new Set<string>()
  const categories: Array<{
    id: number
    name: string
    iconUrl: string
  }> = []

  for (const poi of pois) {
    const name = poi.category?.name
    if (!name || seen.has(name)) continue

    seen.add(name)
    categories.push({
      id: poi.category!.id,
      name,
      iconUrl: getStrapiMedia(poi.category?.icon?.url ?? null),
    })
  }

  return categories
}

export function toExternalUrl(url: string | null | undefined): string | null {
  if (!url) return null
  // Keep hash placeholders / in-page anchors (same as toExternalHref).
  if (url.startsWith('#') || url.startsWith('http') || url.startsWith('/')) {
    return url
  }
  return `https://${url}`
}

export function googleMapsSearchUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
}
